import { ToastService } from "@shared/lib/services";
import { BANKVERIFICATIONSTATUS } from "@shared/lib/types/briefCampains";
import { Form, Input } from "antd";
import { Landmark, X } from "lucide-react";
import React, { useState } from "react";
import { useCheckBankDetails } from "../../../../entities/campaigns/lib/hooks/useCheckBankDetails";
import { useRegisterBankDetails } from "../../../../entities/campaigns/lib/hooks/useRegisterBankDetails";
import styles from "./styles.module.scss";

export interface AddBillingDetailsValues {
  // accountHolderName: string;
  accountNumber: string;
  ifsc: string;
}

export interface AddBillingDetailsProps {
  vendorPaymentId?: string;
  initialValues?: Partial<AddBillingDetailsValues>;
  creatorName?: string;
  campaignName?: string;
  onClose?: () => void;
  onCancel?: () => void;
  onVerify?: (values: AddBillingDetailsValues) => void | Promise<void>;
  onSaveAndQueue?: (values: AddBillingDetailsValues) => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

const ACCOUNT_NUMBER_REGEX = /^\d{9,18}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const maskAccount = (accountNumber?: string): string => {
  if (!accountNumber) return "";
  const last4 = accountNumber.slice(-4);
  return `••••${last4}`;
};

const maskIfsc = (ifsc?: string): string => {
  if (!ifsc) return "";
  const upper = ifsc.toUpperCase();
  if (upper.length >= 7) {
    return `${upper.slice(0, 7)}XXXX`;
  }
  return `${upper}XXXX`;
};

export function AddBillingDetails({
  vendorPaymentId,
  initialValues,
  creatorName,
  campaignName,
  onClose,
  onCancel,
  onVerify,
  onSaveAndQueue,
  loading = false,
  disabled = false,
}: AddBillingDetailsProps) {
  const [form] = Form.useForm<AddBillingDetailsValues>();
  const {
    checkBankDetails,
    isChecking,
    isSuccess,
    isError: isCheckError,
    error: checkError,
    data,
  } = useCheckBankDetails();
  const {
    registerBankDetails,
    isRegistering,
    isRegistered,
    isError: isRegisterError,
    error: registerError,
  } = useRegisterBankDetails();

  const [submittedValues, setSubmittedValues] =
    useState<AddBillingDetailsValues | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);

  const mergedInitialValues = React.useMemo(
    () => ({
      ...initialValues,
    }),
    [initialValues]
  );

  const isSubmitting = loading || isChecking;

  React.useEffect(() => {
    if (isSuccess && data?.data) {
      const resData = data.data;
      const statusStr = String(resData.status || "");
      if (
        resData.failureResponse ||
        statusStr === "Failed" ||
        statusStr === BANKVERIFICATIONSTATUS.Failed
      ) {
        const errorMsg = resData.failureResponse;
        setFailedMessage(errorMsg ?? "Something went wrong.");
        setIsFailed(true);
        setIsVerified(false);
        ToastService.error(errorMsg ?? "Something went wrong.");
      } else {
        ToastService.success("Bank details verified successfully");
        setIsVerified(true);
        setIsFailed(false);
        setFailedMessage(null);
      }
    }
  }, [isSuccess]);

  React.useEffect(() => {
    if (isCheckError && checkError) {
      ToastService.error("Details verification failed.");
    }
  }, [isCheckError, checkError]);

  React.useEffect(() => {
    if (isRegistered) {
      ToastService.success("Bank details saved and queued successfully");
      const currentValues = submittedValues || form.getFieldsValue();
      if (onSaveAndQueue) {
        void onSaveAndQueue(currentValues);
      }
    }
  }, [isRegistered, onSaveAndQueue, submittedValues, form]);

  React.useEffect(() => {
    if (isRegisterError && registerError) {
      ToastService.error("Failed to queue this payout");
    }
  }, [isRegisterError, registerError]);

  const handleFinish = (values: AddBillingDetailsValues) => {
    const sanitizedValues = {
      ...values,
      accountNumber: values.accountNumber.trim(),
      ifsc: values.ifsc.trim().toUpperCase(),
    };
    setSubmittedValues(sanitizedValues);
    setIsFailed(false);
    setFailedMessage(null);
    if (vendorPaymentId) {
      checkBankDetails({
        vendorPaymentId,
        bankAccountNumber: sanitizedValues.accountNumber,
        ifscCode: sanitizedValues.ifsc,
      });
    } else {
      setIsVerified(true);
    }
    if (onVerify) {
      void onVerify(sanitizedValues);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handleEdit = () => {
    setIsVerified(false);
    setIsFailed(false);
    setFailedMessage(null);
  };

  const handleSaveAndQueue = () => {
    const targetPaymentId = vendorPaymentId;

    if (targetPaymentId) {
      registerBankDetails({ vendorPaymentId: targetPaymentId });
    }
  };

  // Extract verified card data from API response or form values
  const verificationData = data?.data;
  const verifiedBankName =
    verificationData?.verificationResponse?.bank_name ||
    verificationData?.verificationResponse?.ifsc_details?.bank ||
    "";

  const verifiedAccountName =
    verificationData?.bankAccountName ||
    verificationData?.verificationResponse?.name_at_bank ||
    "";

  const currentAccountNumber =
    verificationData?.bankAccountNumber || submittedValues?.accountNumber || "";

  const currentIfsc = verificationData?.ifscCode || submittedValues?.ifsc || "";

  const formattedAccountDetails = [
    currentAccountNumber ? maskAccount(currentAccountNumber) : "",
    currentIfsc ? maskIfsc(currentIfsc) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const headerSubText = [creatorName, campaignName].filter(Boolean).join(" · ");

  if (isFailed) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.headerTitle}>Add billing details</h3>
            {headerSubText ? (
              <p className={styles.headerSubtitle}>{headerSubText}</p>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose || handleCancel}
            aria-label="Close"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.failedContent}>
          <div className={styles.failedAlert}>
            <div className={styles.failedAlertTitle}>
              Uh-oh, we couldn’t verify this account
            </div>
            <p className={styles.failedAlertMessage}>{failedMessage}</p>
          </div>

          <div className={styles.verifiedCard}>
            {verifiedBankName ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Bank</div>
                <div className={styles.verifiedValueMain}>
                  {verifiedBankName}
                </div>
              </div>
            ) : null}

            {verifiedAccountName ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Name on account</div>
                <div className={styles.verifiedValueMain}>
                  {verifiedAccountName}
                </div>
              </div>
            ) : null}

            {formattedAccountDetails ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Account</div>
                <div className={styles.verifiedValueSub}>
                  {formattedAccountDetails}
                </div>
              </div>
            ) : null}
          </div>

          <div className={styles.failedFooter}>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleEdit}
            >
              Edit details
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.headerTitle}>Add billing details</h3>
            {headerSubText ? (
              <p className={styles.headerSubtitle}>{headerSubText}</p>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose || handleCancel}
            aria-label="Close"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.confirmContent}>
          <p className={styles.confirmDescription}>
            Confirm the verified account before queuing this payout.
          </p>

          <div className={styles.verifiedCard}>
            {verifiedBankName ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Bank</div>
                <div className={styles.verifiedValueMain}>
                  {verifiedBankName}
                </div>
              </div>
            ) : null}

            {verifiedAccountName ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Name on account</div>
                <div className={styles.verifiedValueMain}>
                  {verifiedAccountName}
                </div>
              </div>
            ) : null}

            {formattedAccountDetails ? (
              <div className={styles.verifiedGroup}>
                <div className={styles.verifiedLabel}>Account</div>
                <div className={styles.verifiedValueSub}>
                  {formattedAccountDetails}
                </div>
              </div>
            ) : null}
          </div>

          <div className={styles.confirmFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleEdit}
              disabled={isRegistering}
            >
              Edit
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSaveAndQueue}
              disabled={loading || isRegistering || disabled}
            >
              {isRegistering ? "Saving..." : "Save and queue"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.headerTitle}>Add billing details</h3>
          {headerSubText ? (
            <p className={styles.headerSubtitle}>{headerSubText}</p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose || handleCancel}
          aria-label="Close"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <Form<AddBillingDetailsValues>
        form={form}
        layout="vertical"
        initialValues={mergedInitialValues}
        onFinish={handleFinish}
        className="add-billing-details-form"
      >
        <div className={styles.formContent}>
          <p className={styles.description}>
            Add bank billing for this pending payout. It will be verified with a
            penny drop, then moved to queued.
          </p>
          <div className={styles.prefillNotice}>
            Prefilling from this creator’s saved billing on file.
          </div>

          {/* <Form.Item
            name="accountHolderName"
            label="Account holder name"
            rules={[
              { required: true, message: "Account holder name is required" },
            ]}
          >
            <Input
              className={styles.inputField}
              placeholder="Account holder name"
            />
          </Form.Item> */}

          <Form.Item
            name="accountNumber"
            label="Account number"
            getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) =>
              (e?.target?.value ?? "").replace(/\D/g, "").slice(0, 18)
            }
            rules={[
              { required: true, message: "Account number is required" },
              {
                pattern: ACCOUNT_NUMBER_REGEX,
                message: "Account number must be 9 to 18 digits",
              },
            ]}
          >
            <Input
              className={styles.inputField}
              placeholder="Enter account number"
              inputMode="numeric"
            />
          </Form.Item>

          <Form.Item
            name="ifsc"
            label="IFSC"
            getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) =>
              (e?.target?.value ?? "")
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "")
                .slice(0, 11)
            }
            rules={[
              { required: true, message: "IFSC is required" },
              {
                pattern: IFSC_REGEX,
                message:
                  "Format: 4 letters, then 0, then 6 letters or digits (e.g. HDFC0001234)",
              },
            ]}
          >
            <Input
              className={`${styles.inputField} ${styles.ifscInput}`}
              placeholder="HDFC0001234"
            />
          </Form.Item>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || disabled}
          >
            <Landmark size={14} aria-hidden="true" />
            {isSubmitting ? "Verifying..." : "Verify account"}
          </button>
        </div>
      </Form>
    </div>
  );
}
